import { translate } from "../../translate/translate";
import BaseModel from "../default/base-model";

export class Analysis extends BaseModel {
  customer?: string;
  description?: string;
  status?: AnalysisStatus | null;
  processing_started_at?: string;
  processing_ended_at?: string;
  processing_duration?: string;
  user_name?: string;
  period?: number;
  effective_period?: number;
  forecast?: number;
  parent_analysis_id?: number;
  is_fully_completed?: boolean;
  analysis_type?: AnalysisType;

  // Custom
  all_dates?: Array<any>;
  boms?: Array<string>;
  forecast_date?: string;
  has_material_status?: boolean;
  material_status_uuid?: string | null;
  material_status_url?: string | null;
  material_status_generated_at?: string | null;
}

export enum AnalysisType {
  FULL = "full",
  PARTIAL = "partial",
}

export enum AnalysisStatus {
  PROCESSING = 1,
  COMPLETED = 2,
  ERROR = 3,
  CANCELED = 4,
  WAITING = 5,
  SAVE_ERROR = 6,
}

export const AnalysisStatusMapper = {
  [AnalysisStatus.PROCESSING]: translate.t("processing_analysis_col"),
  [AnalysisStatus.COMPLETED]: translate.t("concluded_analysis"),
  [AnalysisStatus.ERROR]: translate.t("calculation_error_analysis"),
  [AnalysisStatus.CANCELED]: translate.t("canceled"),
  [AnalysisStatus.WAITING]: translate.t("waiting_analysis"),
  [AnalysisStatus.SAVE_ERROR]: translate.t("save_error_analysis"),
};

export enum AnalysisResultExtractType {
  FORECAST = "forecast",
  STOCK = "stock",
  IN_TRANSIT = "in_transit",
  PROJECTED_INVENTORY = "projected_inventory",
}

export enum AnalysisPeriod {
  FOUR_WEEKS = 4,
  EIGHT_WEEKS = 8,
  TWELVE_WEEKS = 12,
  SIXTEEN_WEEKS = 16,
  TWENTY_FOUR_WEEKS = 24,
  THIRTY_TWO_WEEKS = 32,
  FORTY_EIGHT_WEEKS = 48,
}

/** "consumed" = set aside for use in this line; "leftover" = surplus credited back. */
export type ExtractSource = "consumed" | "leftover";

export interface ExtractItem {
  code: string;
  quantity: number;
  estimated_time_delivery?: Date;
  bom_component?: ExtractBomComponent;
  used_substitute?: boolean;
  substitute_part_number?: string | null;
}

export interface StockExtractItem extends ExtractItem {
  source?: ExtractSource;
}

export interface ForecastExtractItem extends ExtractItem {
  /** Final product that consumed this semi-finished item; null on regular lines. */
  parent_final_product?: string | null;
  /** BOM calculation order. */
  priority?: number;
  /** Component role in the BOM: 1 = primary (PR), 2 = alternative (ALT). */
  purchase_priority?: number | null;
  /** Component consumption order. */
  consumption_priority?: number | null;
}

export interface ExtractBomComponent {
  part_number: string;
  usage_quantity: number;
}

export interface ExtractGroup<T extends ExtractItem = ExtractItem> {
  items: T[];
  total: number;
}

export interface ExtractData<T extends ExtractItem = ExtractItem> {
  name: string;
  group?: ExtractGroup<T>;
}

export interface ForecastExtractData {
  forecast?: ExtractData<ForecastExtractItem>;
  finished_goods?: ExtractData<ForecastExtractItem>;
  total: number;
}

export interface StockAndInTransitExtractData {
  primary?: ExtractData<StockExtractItem>[];
  alternatives?: ExtractData<StockExtractItem>[];
  /** Lent to another BOM — excluded from the total. */
  lent?: ExtractData[];
  total: number;
}

export interface InTransitExtractData {
  primary?: ExtractData[];
  alternatives?: ExtractData[];
  lent?: ExtractData[];
  total: number;
}

export interface ProjectedInventoryExtractData {
  forecast?: ExtractGroup;
  finished_goods?: ExtractGroup;
  stock?: ExtractGroup;
  in_transit?: ExtractGroup;
  /** Unmet demand from the previous week; already subtracted from the total. */
  backlog?: number;
  total: number;
}
